<template>
	<div ref="w" class="timeonlypicker-container">
		<div class="picker">
			<el-time-picker
				ref="tp"
				v-model="userTimeModel"
				placeholder=""
				format="HH:mm"
				:size="size"
				popper-class="time-only-range-picker"
				@change="onTimePicked"
			/>
		</div>
		<div class="button" v-if="clearable">
			<el-link :underline="false" :icon="cancelIcon" @click="onCancelClick" />
		</div>
	</div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Close } from '@element-plus/icons-vue';
import { watch, WatchStopHandle } from 'vue';
import timeOnlyHelper from '@/helpers/timeOnlyHelper';

@Options({	
	props: {
		modelValue: String,
		size: {
			type: String,
			default: 'default'
		},
		clearable: {
			type: Boolean,
			default: false
		}
	},
	emits: ['update:modelValue', 'canceled']
})
export default class TimeOnlyPickerComponent extends Vue {
	/** props */
	clearable!: boolean;
	modelValue!: string;
	size!: string;
	
	/** constants */
	cancelIcon = Close;

	/** view-models */
	userTimeModel: Date | null = null;

	timeWatchSubscription?: WatchStopHandle;

	mounted() {
		this.timeWatchSubscription = watch(
			() => this.modelValue,
			v => {
				if (!v) {
					this.userTimeModel = null;
					return;
				}				
				this.userTimeModel = this.mapTimeOnlyToDate(v);
			});


		if (this.modelValue) {
			this.userTimeModel = this.mapTimeOnlyToDate(this.modelValue);
		}

		this.$nextTick()
			.then(() => {
				const wrapper = this.$refs.w as HTMLElement;
				const dateInput = wrapper.querySelector("input") as HTMLElement;

				if (!dateInput) {
					console.warn('Unable to find el-time-picker date input element');
					return;
				}

				dateInput.addEventListener('keyup', this.onKeyUp);
			});
	}

	unmounted(): void {
		if (this.timeWatchSubscription) {
			this.timeWatchSubscription();
		}
	}

	onTimePicked() {
		this.emitUpdate();
		this.emitChanged();
	}

	onCancelClick() {
		const timePickerComponent = this.$refs.tp! as any;		
		timePickerComponent?.handleClose();
		this.emitCanceled();
	}

	emitUpdate() {		
		if (!this.userTimeModel) {
			this.$emit('update:modelValue', null);
			return;
		}

		const timeOnly = timeOnlyHelper.time(this.userTimeModel, true);
		this.$emit('update:modelValue', timeOnly);
	}

	emitChanged() {		
		this.$nextTick()
			.then(() => {
				if (!this.userTimeModel) {
					this.$emit('update:modelValue', null);
					return;
				}
				const timeOnly = timeOnlyHelper.time(this.userTimeModel, true);
				this.$emit('changed', timeOnly);
			});
	}

	emitCanceled() {
		this.$emit('canceled');
	}

	onKeyUp(e: KeyboardEvent) {
		const key = e?.key?.toLocaleLowerCase();
		if (!key) {
			return;
		}

		if (key == "enter") {
			const timePickerComponent = this.$refs.tp! as any;
			timePickerComponent.handleClose();
			this.emitUpdate();
			this.emitChanged();
		} else if (key == "escape") {
			const timePickerComponent = this.$refs.tp! as any;
			timePickerComponent.handleClose();
			this.emitCanceled();
		}
	}

	mapTimeOnlyToDate(time?: string) : Date {
		if (!time) {
			const now = new Date();
			time = `${now.getMinutes()}:${now.getHours()}:00`
		}

		const [hours, minutes] = time.split(':').map(Number);
		const d = new Date();
		d.setHours(hours as number);
		d.setMinutes(minutes as number);

		return d;
	}
}
</script>

<style>
.timeonlypicker-container {
	display: flex;
	align-items: stretch;
}

	.timeonlypicker-container .picker {
		flex: 80%;	
	}

		.timeonlypicker-container .picker > div {
			width: unset!important;
		}

	.timeonlypicker-container .button {
		flex: 20%;	
	}
</style>